import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Combobox } from './ui/combobox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { ContactSelector } from './Sales/ContactSelector';
import { CustomerDetailsPanel } from './Sales/CustomerDetailsPanel';
import { apiFetch } from "../utils/api";
import { useAuth } from '../Services/AuthContext';
import {
  fetchProcessStages,
  fetchProducts,
  fetchContractTerms,
  fetchSKUTypes,
  fetchProductDescriptions,
  fetchContracts,
  fetchEndUserContacts,
  fetchEndUsers,
  fetchDriverDetailsLookup,
  fetchReasonCodeLookup,
  fetchEvents,
  fetchOpportunities,
  getStageBadgeColor,
  generateOpportunityNumber,
  fetchSegments,
  fetchOpportunityContractTerms,
  fetchCountriesLookup
} from '../Services/SalesRenewalsService';
import { SalesMetrics } from './SalesMetrics';
import { LoadingSpinner } from './ui/spinner'
import {
  Ban,
  Target,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  TrendingUp,
  Calendar as CalendarIcon,
  DollarSign,
  Users,
  Check,
  CheckCircle,
  Circle,
  Info,
  Clock,
  AlertTriangle,
  Building2,
  Download,
  Mail,
  Phone,
  MapPin,
  X,
  XCircle,
  ShoppingCart,
  Calculator,
  Trash2,
  Package,
  Award,
  Repeat,
  Import,
  RefreshCw,
  Loader2,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { useDebounce } from '@uidotdev/usehooks';


import { WizardIndicator } from './WizardIndicator';
import { EndUserAccountsTab } from './SalesRenewalsNew/EndUserAccountsTab';
import { formatZARCurrency } from '../utils/formatUtils';
import { Stage06Dialog } from './SalesRenewalsNew/Dialogs/Stage06Dialog';
import { Stage07Dialog } from './SalesRenewalsNew/Dialogs/Stage07Dialog';
import { AdjustContractDialog } from './SalesRenewalsNew/Dialogs/AdjustContractDialog';
import { CreateEndUserDialog } from './SalesRenewalsNew/Dialogs/CreateEndUserDialog';
import { OpportunityActionDialog } from './SalesRenewalsNew/Dialogs/OpportunityActionDialog';

// Helper function to format Date to YYYY-MM-DD for input type="date"
const formatDateToISO = (date: Date | null) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to get minimum date (today)
const getMinDate = () => {
  const today = new Date();
  return formatDateToISO(today);
};

// Helper function to get maximum date (30 days from today)
const getMaxDate = () => {
  const today = new Date();
  const maxDate = new Date(today.setDate(today.getDate() + 30));
  return formatDateToISO(maxDate);
};

export function SalesRenewals({ setActiveSection, openCreateDialog = false, initialFilterEvent = null }) {
  const [priceList, setPriceLsit] = useState([]);
  const [processStages, setProcessStages] = useState([]);
  const [events, setEvents] = useState([]);
  const [segments, setSegments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [driverDetailoptions, setDriverDetailOptions] = useState([]);
  const [cancelledReasons, setCancelledReasons] = useState([]);
  const [contractTerms, setcontractTerms] = useState([]);
  const [skuTypes, setSkuTypes] = useState([]);
  const [opportunityContractTerms, setOpportunityContractTerms] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(openCreateDialog);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [filterStage, setFilterStage] = useState('all');
  const [filterEvent, setFilterEvent] = useState(initialFilterEvent || 'all');
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [localOpportunities, setLocalOpportunities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [isEditOpportunityOpen, setIsEditOpportunityOpen] = useState(false);

  // Multi-step wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedContact, setSelectedContact] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isDraftSaved, setIsDraftSaved] = useState(false);

  // End User Accounts state
  const [endUserContacts, setEndUserContacts] = useState([]);

  // Dialog for creating end user from opportunity creation
  const [isCreateEndUserFromOpportunityOpen, setIsCreateEndUserFromOpportunityOpen] = useState(false);
  const [localEndUsers, setLocalEndUsers] = useState([]);

  // Stage change confirmation dialogs
  const [isStage06DialogOpen, setIsStage06DialogOpen] = useState(false);
  const [isStage07DialogOpen, setIsStage07DialogOpen] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState<{opportunityId: string, newStage: string} | null>(null);

  const [isAdjustContractDialogOpen, setIsAdjustContractDialogOpen] = useState(false);
  const [proposedContractEndDate, setProposedContractEndDate] = useState<Date | null>(null);

  // Opportunity action dialog state (Accept, Lost, Cancelled)
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'accept' | 'lost' | 'canceled' | null>(null);
  const [selectedOpportunityForAction, setSelectedOpportunityForAction] = useState<any>(null);
  const [selectedReason, setSelectedReason] = useState('');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    perPage: 10,
  });

  const {
    idToken,
    currentPartner,
    setPendingOpportunityItem,
    pendingOpportunityItem,
    currentOpportunityId,
    setCurrentOpportunityId,
    viewDialogEditData,
    setViewDialogEditData,
    isViewOpportunityOpen,
    setIsViewOpportunityOpen,
    editOpportunityData,
    setEditOpportunityData,
    setViewOpportunity,
    viewOpportunity,
    setIsViewDialogInEditMode,
    isViewDialogInEditMode
  } = useAuth();

  // Function to get close date based on transaction start date + 21 days
  const getCloseDateFromStartDate = (startDate: Date) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + 21);
    return date;
  };

  // Handle transaction start date change and auto-update close date
  const handleTransactionStartDateChange = (date: Date) => {
    setNewOpportunity(prev => ({
      ...prev,
      transaction_start_date: date,
      expiry_date: getCloseDateFromStartDate(date)
    }));
  };

  // State for creating new opportunities
  const [newOpportunity, setNewOpportunity] = useState({
    id: '',
    customer: '',
    expiry_date: getCloseDateFromStartDate(new Date()),
    description: '',
    driverType: '',
    driverDetail: '',
    transaction_start_date: new Date(),
    customer_reference: '',
    quoteNumber: null,
    productLines: [],
    contract_end_date: null
  });

  // State for product lines
  const [currentProductLine, setCurrentProductLine] = useState({
    product: '',
    description: '',
    quantity: '1',
    unitPrice: '',
    discount: '0'
  });

  // Product catalog state
  const [productSearch, setProductSearch] = useState('');
  const [itemGroupFilter, setItemGroupFilter] = useState('all');
  const [productGroupFilter, setProductGroupFilter] = useState('all');
  const [contractTermFilter, setContractTermFilter] = useState('all');
  const [skuTypeFilter, setSkuTypeFilter] = useState('all');
  const [productQuantities, setProductQuantities] = useState({});
  const [reviewedProducts, setReviewedProducts] = useState(new Set());
  const [contracts, setsetContracts] = useState([]);
  const [descriptions, setDescriptions] = useState([]);

  const [newEndUser, setNewEndUser] = useState({
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    address_street: '',
    address_city: '',
    address_zip_code: '',
    address_country_region_id: 'ZAF',
    industry: '',
    status: 'Active',
  });

  useEffect(() => {
    fetchEndUsers(idToken, currentPartner, customerSearchTerm, setLocalEndUsers);
    fetchDriverDetailsLookup(idToken, setDriverDetailOptions);
    fetchProcessStages(idToken, setProcessStages);
    fetchProducts(idToken, currentPartner, productSearch, contractTermFilter, productGroupFilter, skuTypeFilter, setPriceLsit);
    fetchOpportunities(idToken, currentPartner, 1, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination);
    fetchEvents(idToken, setEvents);
    fetchContractTerms(idToken, setcontractTerms);
    fetchProductDescriptions(idToken, setDescriptions);
    setSkuTypes(["M2S New","Subscription New"]);
    fetchSegments(idToken, setSegments);
    fetchCountriesLookup(idToken, setCountries);
  }, [])

  // Lazy load cancelled reasons only when action dialog opens
  useEffect(() => {
    if (isActionDialogOpen && cancelledReasons.length === 0) {
      fetchReasonCodeLookup(idToken, setCancelledReasons);
    }
  }, [isActionDialogOpen])

  useEffect(() => {
    fetchEndUsers(idToken, currentPartner, customerSearchTerm, setLocalEndUsers);
    fetchOpportunities(idToken, currentPartner, 1, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination);
  }, [currentPartner])

  useEffect(() => {
    fetchEndUsers(idToken, currentPartner, customerSearchTerm, setLocalEndUsers);
  }, [customerSearchTerm])

  useEffect(() => {
    fetchProducts(idToken, currentPartner, productSearch, contractTermFilter, productGroupFilter, skuTypeFilter, setPriceLsit);
  }, [productSearch, contractTermFilter, productGroupFilter, skuTypeFilter])

  useEffect(() => {
    fetchOpportunities(idToken, currentPartner, 1, pagination.perPage, debouncedSearchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination);
  }, [debouncedSearchTerm, filterStage, filterEvent])

  useEffect(() => {
    if (selectedCustomer) {
      fetchContracts(idToken, currentPartner, selectedCustomer, setsetContracts);
    }
  }, [selectedCustomer])

  // Handle opening create dialog when prop changes
  useEffect(() => {
    if (openCreateDialog) {
      console.log('Opening create dialog, pending item:', pendingOpportunityItem);
      setIsCreateDialogOpen(true);

      // Check if there's a pending item to add
      const hasPendingItem = !!pendingOpportunityItem;
      const initialProductLines = [];

      if (pendingOpportunityItem) {
        console.log('Adding pending item to product lines:', pendingOpportunityItem);
        const newLine = {
          id: Date.now().toString(),
          product_name: pendingOpportunityItem.product_name,
          description: pendingOpportunityItem.description,
          quantity: 1,
          unit_price: parseFloat(pendingOpportunityItem.unit_price.replace(/,/g, '')),
          discount: 0,
          total: parseFloat(pendingOpportunityItem.unit_price.replace(/,/g, '')),
          item_id: pendingOpportunityItem.item_id,
          contract_term: pendingOpportunityItem.contract_term,
          item_group: pendingOpportunityItem.item_group,
          needsPriceReview: false
        };
        initialProductLines.push(newLine);
      }

      // Reset wizard state - start at Step 3 if we have a pending item, otherwise Step 1
      setCurrentStep(1);
      setSelectedCustomer(null);
      setSelectedContact(null);
      setCustomerSearchTerm('');
      setIsDraftSaved(false);
      setReviewedProducts(new Set());
      const defaultStartDate = new Date();

      setNewOpportunity({
        id: generateOpportunityNumber(currentPartner),
        expiry_date: getCloseDateFromStartDate(defaultStartDate),
        partner_comments: '',
        driverType: '',
        transaction_start_date: defaultStartDate,
        customer_reference: '',
        productLines: initialProductLines
      });
    }
  }, []);

  // Reset wizard when dialog closes
  const resetWizard = () => {
    setCurrentStep(1);
    setSelectedCustomer(null);
    setSelectedContact(null);
    setCustomerSearchTerm('');
    setIsDraftSaved(false);
    setReviewedProducts(new Set());
    const defaultStartDate = new Date();

    setNewOpportunity({
      id: '',
      name: '',
      email: '',
      expiry_date: getCloseDateFromStartDate(defaultStartDate),
      transaction_start_date: defaultStartDate,
      customer_reference: '',
      quoteNumber: null,
      productLines: [],
      contract_end_date: null
    });
    setCurrentProductLine({
      product: '',
      description: '',
      quantity: '1',
      unitPrice: '',
    });
  };

  // Handle step navigation
  const nextStep = () => {
    // Validate required fields on Step 2
    if (currentStep === 2) {
      if (!newOpportunity.driver_id) {
        alert('Please select a Driver Detail before proceeding.');
        return;
      }
      if (!newOpportunity.event) {
        alert('Please select an Autodesk Event before proceeding.');
        return;
      }
    }

    console.debug('Moving to next step from step', newOpportunity);
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Save as draft functionality
  const saveAsDraft = () => {
    setIsDraftSaved(true);
  };

  // Handle stage change
  const handleStageChange = async (id, newStage, opportunity_id) => {
    const opportunity = localOpportunities.find(opp => opp.opportunity_id === opportunity_id);
    setLocalOpportunities(prevOpps =>
      prevOpps.map(opp =>
        opp.id === id ? { ...opp, process_stage_name: newStage } : opp
      )
    );

    // If changing to stage 06 (Closed Won), show acceptance dialog
    if (newStage === '06-Closed / Won') {
      setPendingStageChange({ opportunity_id, newStage });
      setSelectedOpportunityForAction(opportunity);
      setIsStage06DialogOpen(true);
      return;
    }
    
    // If changing to stage 07 (Closed Lost), show lost/cancelled dialog
    if (newStage === '07-Closed / Lost - Cancelled') {
      setPendingStageChange({ opportunity_id, newStage });
      setSelectedOpportunityForAction(opportunity);
      setIsStage07DialogOpen(true);
      return;
    }

    if (newStage != '06-Closed / Won' && newStage != '07-Closed / Lost - Cancelled') {
      const res = await apiFetch(await idToken(), "/api/opportunities/update_stage", {
        method: "PATCH",
        body: JSON.stringify({ partner_id: currentPartner.partner_id, company_id: currentPartner.company_id, opportunity_id: opportunity_id, process_stage_name: newStage })
      });
    }

  };

  // Handle confirming Lost or Cancelled action
  const handleConfirmAction = async () => {
    if (!selectedReason) {
      alert('Please select a reason');
      return;
    }

    if (selectedAction === 'lost') {
      setLocalOpportunities(prevOpps =>
        prevOpps.map(opp =>
          opp.opportunity_id === selectedOpportunityForAction.opportunity_id 
            ? { ...opp, stage: '07-Closed / Lost - Cancelled', lostReason: selectedReason } 
            : opp
        )
      );
      toast.error('Opportunity marked as Lost', {
        description: `${selectedOpportunityForAction.id} - Reason: ${selectedReason}`
      });
    } else if (selectedAction === 'canceled') {
      setLocalOpportunities(prevOpps =>
        prevOpps.map(opp =>
          opp.opportunity_id === selectedOpportunityForAction.opportunity_id 
            ? { ...opp, stage: '07-Closed / Lost - Cancelled', cancelledReason: selectedReason } 
            : opp
        )
      );
      toast.error('Opportunity cancelled', {
        description: `${selectedOpportunityForAction.id} - Reason: ${selectedReason}`
      });
    }

    const res = await apiFetch(await idToken(), "/api/opportunities/update_stage", {
      method: "PATCH",
      body: JSON.stringify({ partner_id: currentPartner.partner_id, company_id: currentPartner.company_id, opportunity_id: pendingStageChange.opportunity_id, process_stage_name: '07-Closed / Lost - Cancelled', portal_status: selectedAction, reason_code: selectedReason })
    });

    // Reset and close dialog
    setIsActionDialogOpen(false);
    setSelectedAction(null);
    setSelectedOpportunityForAction(null);
    setSelectedReason('');
    setPendingStageChange(null);
  };

  // Handle Stage 06 (Closed Won) - Accept Quote confirmation
  const handleStage06Accept = async () => {
    if (pendingStageChange) {
      setLocalOpportunities(prevOpps =>
        prevOpps.map(opp =>
          opp.opportunity_id === pendingStageChange.opportunity_id 
            ? { ...opp, stage: '06-Closed / Won', portal_status: 'Accepted' } 
            : opp
        )
      );
      toast.success('Quote accepted', {
        description: `${selectedOpportunityForAction?.opportunity_id} has been marked as Closed Won.`
      });
    }
    
    const res = await apiFetch(await idToken(), "/api/opportunities/update_stage", {
      method: "PATCH",
      body: JSON.stringify({ partner_id: currentPartner.partner_id, company_id: currentPartner.company_id, opportunity_id: pendingStageChange.opportunity_id, process_stage_name: '06-Closed / Won', portal_status: 'Accepted' })
    });

    setIsStage06DialogOpen(false);
    setPendingStageChange(null);
    setSelectedOpportunityForAction(null);
  };

  const handleStage06Decline = () => {
    // Just close the dialog without updating
    setIsStage06DialogOpen(false);
    setPendingStageChange(null);
    setSelectedOpportunityForAction(null);
    fetchOpportunities(idToken, currentPartner, 1, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination);
  };

  // Handle Stage 07 (Closed Lost) - Lost or Cancelled selection
  const handleStage07Lost = () => {
    setIsStage07DialogOpen(false);
    setSelectedAction('lost');
    setSelectedReason('');
    setIsActionDialogOpen(true);
  };

  const handleStage07Cancelled = () => {
    setIsStage07DialogOpen(false);
    setSelectedAction('canceled');
    setSelectedReason('');
    setIsActionDialogOpen(true);
  };

  const handleStage07Cancel = () => {
    // Just close the dialog without updating
    setIsStage07DialogOpen(false);
    setPendingStageChange(null);
    setSelectedOpportunityForAction(null);
    fetchOpportunities(idToken, currentPartner, 1, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination);
    console.debug('Cancelled Stage 07 dialog, no changes made.');
  };

  // Handle view opportunity
  const handleViewOpportunity = (opp) => {
    setViewOpportunity(opp);
    setIsViewOpportunityOpen(true);
    setIsViewDialogInEditMode(false);
    setViewDialogEditData(null);
  };

  // Handle edit opportunity
  const handleEditOpportunity = (opp) => {
    setEditOpportunityData({ ...opp });
    setIsEditOpportunityOpen(true);
  };

  // Handle save edited opportunity
  const handleSaveEditedOpportunity = () => {
    setLocalOpportunities(prevOpps =>
      prevOpps.map(opp =>
        opp.id === editOpportunityData.id ? editOpportunityData : opp
      )
    );
    setIsEditOpportunityOpen(false);
    setEditOpportunityData(null);
  };

  // Handle enabling edit mode in view dialog
  const handleEnableViewDialogEdit = () => {
    setViewDialogEditData({
      ...viewOpportunity,
      productLines: JSON.parse(JSON.stringify(viewOpportunity.productLines || [])) // Deep copy
    });
    setIsViewDialogInEditMode(true);
    fetchEndUserContacts(idToken, currentPartner, viewOpportunity.end_user_id, setEndUserContacts);
  };

  // Handle canceling edit mode in view dialog
  const handleCancelViewDialogEdit = () => {
    setIsViewDialogInEditMode(false);
    setViewDialogEditData(null);
  };

  const handleAdjustContractDate = () => {
    if (proposedContractEndDate) {
      const endDate = new Date(viewDialogEditData.contract_end_date);
      const startDate = new Date(proposedContractEndDate);
      
      let yearDiff = endDate.getFullYear() - startDate.getFullYear();
      let monthDiff = endDate.getMonth() - startDate.getMonth();
      let dayDiff = endDate.getDate() - startDate.getDate();

      let months = yearDiff * 12 + monthDiff;
      if (dayDiff < 0) {
          months--;
      }
      
      const newMonths = Math.abs(months);

      console.debug('Adjusting contract end date to:', proposedContractEndDate, 'which results in months:', startDate, endDate, newMonths);

      const newData = {
        ...viewDialogEditData,
        proposed_contract_end_date: proposedContractEndDate,
        lines: viewDialogEditData.lines.map(line => ({
          ...line,
          months: newMonths
        }))
      };

      handleSaveViewDialogEdit(newData);
      setIsAdjustContractDialogOpen(false);
      setProposedContractEndDate(null);
    }
  };

  // Handle saving changes from view dialog edit mode
  const handleSaveViewDialogEdit = async (data) => {
    setIsSaving(true);
    setLocalOpportunities(prevOpps =>
      prevOpps.map(opp =>
        opp.id === data.id ? data : opp
      )
    );

    console.debug('Saving edited opportunity data:');
    let callUpdateSynchronously = false;
    try {
      const res = await apiFetch(await idToken(), `/api/opportunities/update_opportunity${callUpdateSynchronously ? '?synchronous=true' : ''}`, {
        method: "PATCH",
        body: JSON.stringify(data)
      });
      console.debug('Updated opportunity response:', res.data);
      setViewOpportunity(res.data);
      setIsViewDialogInEditMode(false);
      setViewDialogEditData(res.data);
      fetchOpportunities(idToken, currentPartner, pagination.currentPage, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle updating product line quantity in edit mode
  const handleUpdateProductLineQuantity = (lineId, newQuantity) => {
    setViewDialogEditData((prev: any) => ({
      ...prev,
      lines: prev.lines.map((line) =>
        line.id === lineId
          ? {
            ...line,
            qty: newQuantity,
            amount: newQuantity * Number(line.price)
          }
          : line
      )
    }));
  };

  // Handle updating product line price review status in edit mode
  const handleTogglePriceReview = (lineId: string) => {
    setViewDialogEditData((prev: any) => ({
      ...prev,
      lines: prev.lines.map((line) =>
        line.id === lineId
          ? { ...line, price_review: !line.price_review }
          : line
      )
    }));
  };

  // Handle removing product line in edit mode
  const handleRemoveProductLine = async (lineId) => {
    setViewDialogEditData((prev) => ({
      ...prev,
      lines: prev.lines.map((line) =>
        line.id === lineId
          ? { ...line, qty: 0 }
          : line
      )
    }));
  };

  // Handle copying text to clipboard
  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Handle creating new end user from opportunity creation
  const handleCreateEndUserFromOpportunity = async () => {
    // Validate required fields
    if (!newEndUser.name || !newEndUser.first_name || !newEndUser.email) {
      alert('Please fill in all required fields (Company Name, Contact Person, Email, CSN)');
      return;
    }

    const newId = `EU_${crypto.randomUUID()}`;

    // Create the new end user object with primary contact
    const createdEndUser = {
      id: newId,
      end_user_id: newId,
      name: newEndUser.name,
      first_name: newEndUser.first_name,
      last_name: newEndUser.last_name,
      email: newEndUser.email,
      phone: newEndUser.phone_number || '+27 (11) 000-0000',
      address_street: newEndUser.address_street,
      address_city: newEndUser.address_city,
      address_zip_code: newEndUser.address_zip_code,
      address_country_region_id: newEndUser.address_country_region_id,
      segment_id: newEndUser.segment_id,
      location: `${newEndUser.address_street}, ${newEndUser.address_city}, ${newEndUser.address_zip_code}, ${newEndUser.address_country_region_id}`,
      industry: newEndUser.industry,
      licenses: newEndUser.licenses,
      status: newEndUser.status,
      contacts: [
        {
          id: `contact-${newId}-1`,
          last_name: newEndUser.last_name,
          first_name: newEndUser.first_name,
          email: newEndUser.email,
          phone: newEndUser.phone_number || '+27 (11) 000-0000',
          role: 'Primary Contact',
          isPrimary: true
        }
      ]
    };

    const res = await apiFetch(await idToken(), `/api/end_users?partner_id=${currentPartner.partner_id}&company_id=${currentPartner.company_id}`, {
      method: "POST",
      body: JSON.stringify(createdEndUser)
    });
    createdEndUser.id = res.data.id;

    // Add to local end users list
    setLocalEndUsers([...localEndUsers, createdEndUser]);

    // Auto-select the newly created end user
    setSelectedCustomer(createdEndUser);
    setSelectedContact({});

    // Reset form
    setNewEndUser({
      name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      address_street: '',
      address_city: '',
      address_zip_code: '',
      address_country_region_id: 'ZAF',
      industry: '',
      licenses: 0,
      status: 'Active',
      csn: ''
    });

    // Close dialog
    setIsCreateEndUserFromOpportunityOpen(false);
  };

  // Product line calculations
  const calculateLineTotal = (quantity: number, unitPrice: number, discount: number) => {
    const subtotal = quantity * unitPrice;
    const discountAmount = subtotal * (discount / 100);
    return subtotal - discountAmount;
  };

  const calculateOpportunityTotal = () => {
    return newOpportunity.productLines.reduce((total, line) => total + line.total, 0);
  };

  // Remove product line
  const removeProductLine = (id: string) => {
    setNewOpportunity({
      ...newOpportunity,
      productLines: newOpportunity.productLines.filter(line => line.id !== id)
    });
  };

  // Handle create opportunity
  const handleCreateOpportunity = async () => {
    const res = await apiFetch(await idToken(), "/api/opportunities/create_opportunity", {
      method: "PUT",
      body: JSON.stringify(
        {
          ...newOpportunity,
          end_user_id: selectedCustomer.end_user_id,
          contact_person_id: selectedContact,
          eu_id: selectedCustomer.id
        })
    });

    setIsCreateDialogOpen(false);
    resetWizard();
    fetchOpportunities(idToken, currentPartner, 1, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination);
  };

  // Format date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Toggle price review status for a product
  const togglePriceReview = (productId: string) => {
    const newReviewedProducts = new Set(reviewedProducts);
    if (newReviewedProducts.has(productId)) {
      newReviewedProducts.delete(productId);
    } else {
      newReviewedProducts.add(productId);
    }
    setReviewedProducts(newReviewedProducts);
  };

  // Add product from catalog to opportunity
  const addProductToOpportunity = (product: any) => {
    if (opportunityContractTerms.length > 0 && !opportunityContractTerms.includes(product.contract_term)) {
      toast.error('Product contract term mismatch', {
        description: `Only products with a term of '${opportunityContractTerms.join(', ')}' can be added to this opportunity.`
      });
      return;
    }

    if (newOpportunity.productLines.length > 0) {
      const firstLineContractTerm = newOpportunity.productLines[0].contract_term;
      if (firstLineContractTerm !== product.contract_term) {
        toast.error('Product contract term mismatch', {
          description: `Only products with a term of '${firstLineContractTerm}' can be added to this opportunity. Existing products have a different term.`
        });
        return;
      }
    }
    
    let unit_price = product.unit_price;
    let months = null;

    if(newOpportunity.contract_end_date != null) {
      const period = product.contract_term === 'Annual' ? 12 : 36;
      const startDate = new Date(newOpportunity.transaction_start_date);
      const endDate = new Date(newOpportunity.contract_end_date);
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      months = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30.44)); // Average month length
      unit_price = (product.unit_price / period) * months;
    }

    const quantity = parseInt(productQuantities[product.id]) || 1;
    const newLine = {
      id: Date.now().toString(),
      product_name: product.product_name,
      description: product.description || '',
      quantity,
      unit_price: unit_price,
      discount: 0,
      total: quantity * unit_price,
      autodesk_sku_type: product.autodesk_sku_type,
      contract_term: product.contract_term,
      item_group: product.item_group,
      item_id: product.item_id,
      price_review: reviewedProducts.has(product.id),
      autodesk_months: product.contract_term,
      months: months
    };

    setNewOpportunity({
      ...newOpportunity,
      productLines: [...newOpportunity.productLines, newLine]
    });

    // Reset quantity for this product
    setProductQuantities({
      ...productQuantities,
      [product.id]: ''
    });
  };

  const renderPageNumbers = () => {
    const { currentPage, totalPages } = pagination;
    if (totalPages <= 1) return null;

    const pageNumbers = new Set();
    pageNumbers.add(1);
    pageNumbers.add(totalPages);

    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      if (i > 0 && i <= totalPages) {
        pageNumbers.add(i);
      }
    }

    const sortedPages = Array.from(pageNumbers).sort((a, b) => a - b);
    const renderedPages = [];
    let lastPage = 0;

    for (const page of sortedPages) {
      if (lastPage > 0 && page - lastPage > 1) {
        renderedPages.push('...');
      }
      renderedPages.push(page);
      lastPage = page;
    }

    return renderedPages.map((page, index) =>
      typeof page === 'number' ? (
        <Button
          key={index}
          variant={currentPage === page ? 'default' : 'outline'}
          size="sm"
          onClick={() => fetchOpportunities(idToken, currentPartner, page, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination)}
          className={currentPage === page ? 'bg-worldsview-blue hover:bg-worldsview-blue/90' : ''}
          disabled={isLoading}
        >
          {page}
        </Button>
      ) : (
        <span key={index} className="px-2 py-1 text-sm flex items-center">
          {page}
        </span>
      )
    );
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterStage('all');
    setFilterEvent('all'); // Reset event filter
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>Sales & Renewals</h1>
            <p className="text-muted-foreground mt-2">
              Manage opportunities, track renewals, and close deals
            </p>
          </div>
        </div>
      </div>

      <SalesMetrics setViewOpportunity={setViewOpportunity} setIsViewOpportunityOpen={setIsViewOpportunityOpen}></SalesMetrics>

      <Tabs defaultValue="opportunities" className="space-y-6">
        <div className="glass-card">
          <CardContent className="pt-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="opportunities" className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Opportunities
              </TabsTrigger>
              <TabsTrigger value="end-users" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                End User Accounts
              </TabsTrigger>
            </TabsList>
          </CardContent>
        </div>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-worldsview-orange" />
                  Sales Opportunities
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => fetchOpportunities(idToken, currentPartner, pagination.currentPage, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination)}
                    disabled={isLoading}
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
                    setIsCreateDialogOpen(open);
                    if (!open) resetWizard();
                  }}>
                    <DialogTrigger asChild>
                      <Button
                        className="bg-worldsview-orange hover:bg-worldsview-orange/90"
                        onClick={() => {
                          resetWizard();
                          setNewOpportunity(prev => ({
                            ...prev,
                            id: generateOpportunityNumber(currentPartner)
                          }));
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Opportunity
                      </Button>
                    </DialogTrigger>
                    <DialogContent
                      className="!w-[90vw] !max-w-none max-h-[95vh] overflow-y-auto glass-modal"
                      style={{ width: '90vw', maxWidth: 'none' }}
                      onInteractOutside={(e) => e.preventDefault()}
                    >
                      <DialogHeader>
                        <DialogTitle className="text-2xl">Create New Opportunity</DialogTitle>
                        <DialogDescription className="text-base">
                          Create a new opportunity by following the step-by-step process below.
                        </DialogDescription>
                      </DialogHeader>

                      {/* Multi-Step Progress Indicator */}
                      <WizardIndicator currentStep={currentStep} />

                      {/* Step Content */}
                      <div className="min-h-[400px]">
                        {/* Step 1: Account Selection */}
                        {currentStep === 1 && (
                          <div className="space-y-6">
                            <div>
                              <h3 className="text-lg font-semibold mb-2">Step 1: End User Account Selection</h3>
                              <p className="text-muted-foreground mb-6">Search and select an end user account for this opportunity.</p>
                            </div>

                            <div className="grid grid-cols-3 gap-6">
                              {/* End User Search and List */}
                              <div className="col-span-2 space-y-4">
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between mb-2">
                                    <Label>Search End User Account</Label>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setIsCreateEndUserFromOpportunityOpen(true)}
                                      className="border-worldsview-green text-worldsview-green hover:bg-worldsview-green/10"
                                    >
                                      <Plus className="w-4 h-4 mr-2" />
                                      Create New End User
                                    </Button>
                                  </div>
                                  <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                      placeholder="Search by end user company, account ID, CSN, or contact person..."
                                      value={customerSearchTerm}
                                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                      className="pl-10 glass-input"
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2 max-h-[320px] overflow-y-auto">
                                  {localEndUsers?.map((endUser) => (
                                    <Card
                                      key={`end_user_${endUser.id}_${endUser.end_user_id}_${endUser.id}`}
                                      className={`glass-card cursor-pointer transition-all hover:scale-[1.02] ${selectedCustomer?.id === endUser.id ? 'ring-2 ring-worldsview-blue bg-worldsview-blue/10' : ''
                                        }`}
                                      onClick={() => {
                                        setSelectedCustomer(endUser);
                                        setSelectedContact(endUser.contacts[0]?.person_id);
                                      }}
                                    >
                                      <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-3">
                                              <div className="w-8 h-8 bg-worldsview-blue/20 rounded-full flex items-center justify-center">
                                                <Building2 className="w-4 h-4 text-worldsview-blue" />
                                              </div>
                                              <div>
                                                <h4 className="font-medium">{endUser.name}</h4>
                                                <p className="text-sm text-muted-foreground">{endUser.end_user_id} | CSN: {endUser.csn}</p>
                                              </div>
                                            </div>

                                          </div>
                                          <Badge
                                            variant="outline"
                                            className={
                                              endUser.status === 'Active'
                                                ? 'bg-worldsview-green/10 text-worldsview-green border-worldsview-green/20'
                                                : endUser.status === 'Pending Renewal'
                                                  ? 'bg-worldsview-orange/10 text-worldsview-orange border-worldsview-orange/20'
                                                  : 'bg-worldsview-blue/10 text-worldsview-blue border-worldsview-blue/20'
                                            }
                                          >
                                            {endUser.status}
                                          </Badge>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>

                              {/* Selected Customer Details */}
                              <div className="space-y-4">
                                <CustomerDetailsPanel selectedCustomer={selectedCustomer} />
                              </div>
                            </div>

                            <div className="flex justify-between pt-6">
                              <Button variant="outline" disabled>
                                Previous
                              </Button>
                              <Button
                                onClick={nextStep}
                                disabled={!selectedCustomer}
                                className="bg-worldsview-blue hover:bg-worldsview-blue/90"
                              >
                                Next: Opportunity Setup
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Step 2: Opportunity Setup */}
                        {currentStep === 2 && (
                          <div className="space-y-6">
                            <div>
                              <h3 className="text-lg font-semibold mb-2">Step 2: Opportunity Setup</h3>
                              <p className="text-muted-foreground mb-6">Configure the basic details for this opportunity.</p>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <Label htmlFor="opportunity-id">Opportunity ID</Label>
                                  <Input
                                    id="opportunity-id"
                                    value={newOpportunity.id}
                                    disabled
                                    className="glass-input bg-muted/20"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor="customer-name">Customer</Label>
                                  <div className="glass-input px-3 py-2 bg-muted/20 text-muted-foreground">
                                    {selectedCustomer ? selectedCustomer.name : 'No customer selected'}
                                  </div>
                                </div>

                                <ContactSelector
                                  selectedCustomer={selectedCustomer}
                                  selectedContact={selectedContact}
                                  onContactChange={setSelectedContact}
                                />

                                <div className="space-y-2">
                                  <Label htmlFor="customer-reference">Customer Reference</Label>
                                  <Input
                                    id="customer-reference"
                                    placeholder="Customer reference number or ID"
                                    value={newOpportunity.customer_reference}
                                    onChange={async (e) => setNewOpportunity({ ...newOpportunity, customer_reference: e.target.value })}
                                    className="glass-input"
                                  />
                                </div>
                              </div>

                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <Label htmlFor="driver-detail">Driver Detail <span className="text-worldsview-red">*</span></Label>
                                  <Select
                                    value={newOpportunity.driver_id}
                                    onValueChange={(value) => setNewOpportunity({ ...newOpportunity, driver_id: value })}
                                  >
                                    <SelectTrigger className="glass-input">
                                      <SelectValue placeholder="Select driver detail" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {driverDetailoptions.map((option) => (
                                        <SelectItem key={option.id} value={option.name}>
                                          {option.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor="autodesk-event">Autodesk Event<span className="text-worldsview-red">*</span></Label>
                                  <Select
                                    value={newOpportunity.event}
                                    onValueChange={(value) => {
                                      if (value !== 'Additions') {
                                        setNewOpportunity({ ...newOpportunity, event: value, contract: null, contract_end_date: null })
                                      } else {
                                        setNewOpportunity({ ...newOpportunity, event: value })
                                      }
                                    }
                                    }
                                  >
                                    <SelectTrigger className="glass-input">
                                      <SelectValue placeholder="Select Autodesk event" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Initial order">Initial order</SelectItem>
                                      <SelectItem value="Additions">Addition</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {newOpportunity.event === 'Additions' && (
                                    <div className="space-y-2 mt-3">
                                      <Label htmlFor="contract-number">
                                        Contract Number
                                        {newOpportunity.event === 'Additions' && <span className="text-worldsview-red ml-1">*</span>}
                                      </Label>

                                    <Combobox
                                        options={contracts.map((contract) => ({
                                            value: contract.contract_number,
                                            label: contract.contract_number,
                                        }))}
                                        value={newOpportunity.contract}
                                        onValueChange={(value) => {
                                            const selectedContractObject = contracts.find(
                                                (contract) => contract.contract_number === value
                                            );
                                            setNewOpportunity({
                                                ...newOpportunity,
                                                contract: value,
                                                contract_end_date: selectedContractObject ? selectedContractObject.end_date : null
                                            });
                                            if (value) {
                                              fetchOpportunityContractTerms(idToken, currentPartner.partner_id, currentPartner.company_id, value, setOpportunityContractTerms);
                                            } else {
                                              setOpportunityContractTerms([]);
                                            }
                                        }}
                                        placeholder="Select contract"
                                        emptyMessage="No contract found."
                                        searchPlaceholder="Search contracts..."
                                    />

                                      {newOpportunity.contract && (
                                        <div className="grid grid-cols-2 gap-4">
                                          <div className="space-y-2">
                                            <Label>Contract Start Date</Label>
                                            <div className="glass-input px-3 py-2 bg-muted/20 text-muted-foreground text-sm">
                                              {newOpportunity.contract ?
                                                contracts.filter((it) => it.contract_number == newOpportunity.contract)[0].start_date : 'N/A'
                                              }
                                            </div>
                                          </div>
                                          <div className="space-y-2">
                                            <Label>Contract End Date</Label>
                                            <div className="glass-input px-3 py-2 bg-muted/20 text-muted-foreground text-sm">
                                              {newOpportunity.contract ?
                                                contracts.filter((it) => it.contract_number == newOpportunity.contract)[0].end_date : 'N/A'
                                              }
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label>Transaction Start Date</Label>
                                    <Input
                                      type="date"
                                      value={formatDateToISO(newOpportunity.transaction_start_date)}
                                      onChange={(e) => {
                                        const date = new Date(e.target.value);
                                        handleTransactionStartDateChange(date);
                                      }}
                                      min={getMinDate()}
                                      max={getMaxDate()}
                                      className="glass-input"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <Label>Expected Close Date</Label>
                                    <Input
                                      type="date"
                                      min={getMinDate()}
                                      value={formatDateToISO(newOpportunity.expiry_date)}
                                      onChange={(e) => {
                                        const date = new Date(e.target.value);
                                        setNewOpportunity({ ...newOpportunity, expiry_date: date });
                                      }}
                                      className="glass-input"
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor="customer-reference">Partner Reference</Label>
                                  <Input
                                    id="partner-reference"
                                    placeholder="Partner reference number or ID"
                                    value={newOpportunity.partner_reference}
                                    onChange={async (e) => setNewOpportunity({ ...newOpportunity, partner_reference: e.target.value })}
                                    className="glass-input"
                                  />
                                </div>

                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="description">Partner Comments</Label>
                              <Textarea
                                id="description"
                                placeholder="Add any partner-specific comments or notes about this opportunity"
                                value={newOpportunity.partner_comments}
                                onChange={(e) => setNewOpportunity({ ...newOpportunity, partner_comments: e.target.value })}
                                className="glass-input resize-none"
                                rows={4}
                              />
                            </div>

                            <div className="flex justify-between pt-6">
                              <Button variant="outline" onClick={prevStep}>
                                Previous
                              </Button>
                              <div className="flex gap-2">
                                {/* <Button variant="outline" onClick={saveAsDraft}>
                                  Save as Draft
                                </Button> */}
                                <Button
                                  onClick={nextStep}
                                  className="bg-worldsview-blue hover:bg-worldsview-blue/90"
                                >
                                  Next: Product Lines
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Step 3: Product Lines */}
                        {currentStep === 3 && (
                          <div className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                            <div>
                              <h3 className="text-lg font-semibold mb-2">Step 3: Product Lines</h3>
                              <p className="text-muted-foreground mb-6">Add products and services to this opportunity.</p>
                            </div>

                            {/* Info banner when product was added from Price Lookup */}
                            {newOpportunity.productLines.length > 0 && !selectedCustomer && (
                              <div className="glass-card p-4 border-l-4 border-worldsview-blue">
                                <div className="flex items-start gap-3">
                                  <Info className="w-5 h-5 text-worldsview-blue mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="font-medium text-worldsview-blue mb-1">Product Added from Price Lookup</p>
                                    <p className="text-sm text-muted-foreground">
                                      Your selected product has been added below. You can add more products from the catalog, then go back to complete customer details in Steps 1 and 2.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Add Product Line Form */}
                            <Card className="glass-card">
                              <CardHeader>
                                <div className="flex justify-between items-center">
                                  <CardTitle className="text-lg">Product Catalog</CardTitle>
                                  <div className="flex flex-wrap gap-2">
                                    <div className="relative">
                                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                      <Input
                                        placeholder="Search products..."
                                        value={productSearch}
                                        onChange={(e) => setProductSearch(e.target.value)}
                                        className="glass-input pl-10 w-64"
                                      />
                                    </div>
                                    <Select value={productGroupFilter} onValueChange={setProductGroupFilter}>
                                      <SelectTrigger className="glass-input w-48">
                                        <Filter className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder="Product Line Description" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="all">All Product Descriptions</SelectItem>
                                        {descriptions.map((option) => (
                                          <SelectItem key={option} value={option}>
                                            {option}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Select value={contractTermFilter} onValueChange={setContractTermFilter}>
                                      <SelectTrigger className="glass-input w-48">
                                        <Filter className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder="Contract Term" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="all">All Contract Terms</SelectItem>
                                        {contractTerms.map((option) => (
                                          <SelectItem key={option} value={option}>
                                            {option}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Select value={skuTypeFilter} onValueChange={setSkuTypeFilter}>
                                      <SelectTrigger className="glass-input w-48">
                                        <Filter className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder="SKU Type ID" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="all">All SKU Type</SelectItem>
                                        {skuTypes.map((option) => (
                                          <SelectItem key={option} value={option}>
                                            {option}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="border border-glass-border rounded-lg overflow-hidden">
                                  <div className="max-h-[300px] overflow-x-auto overflow-y-auto">
                                    <Table className="min-w-full">
                                      <TableHeader className="sticky top-0 bg-background/95 backdrop-blur-sm z-10">
                                        <TableRow className="border-glass-border">
                                          <TableHead className="w-28 min-w-28">SKU</TableHead>
                                          <TableHead className="w-120 min-w-16">Product Name</TableHead>
                                          <TableHead className="w-16 min-w-16">Term</TableHead>
                                          <TableHead className="w-16 min-w-16">Product Line Description</TableHead>
                                          <TableHead className="w-24 min-w-24">SKU Type Id</TableHead>
                                          <TableHead className="w-16 min-w-16 text-right">Unit Price</TableHead>
                                          <TableHead className="w-12 min-w-10 text-left">Seats</TableHead>
                                          <TableHead className="w-12 min-w-10 text-center">Price Review</TableHead>
                                          <TableHead className="w-16 min-w-16"></TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {priceList.map((product) => (
                                          <TableRow key={product.id} className="border-glass-border">
                                            <TableCell className="font-mono text-sm">{product.item_id}</TableCell>
                                            <TableCell className="max-w-120">
                                              <div className="truncate font-medium" title={product.product_name}>
                                                {product.product_name}
                                              </div>
                                            </TableCell>
                                            <TableCell>
                                              <Badge
                                                variant="outline"
                                                className={`glass-badge text-xs font-medium ${product.contract_term === 'Monthly' ? 'bg-worldsview-blue/10 border-worldsview-blue text-worldsview-blue' :
                                                  product.contract_term === 'Annual' ? 'bg-worldsview-green/10 border-worldsview-green text-worldsview-green' :
                                                    product.contract_term === '2 Year' ? 'bg-worldsview-purple/10 border-worldsview-purple text-worldsview-purple' :
                                                      product.contract_term === '3-Year' ? 'bg-worldsview-orange/10 border-worldsview-orange text-worldsview-orange' :
                                                        'bg-muted/10 border-muted-foreground text-muted-foreground'
                                                  }`}
                                              >
                                                {product.contract_term}
                                              </Badge>
                                            </TableCell>
                                            <TableCell className="max-w-20">
                                              <div className="truncate font-mono text-xs text-muted-foreground" title={product.description}>
                                                {product.description}
                                              </div>
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-muted-foreground">
                                              {product.autodesk_sku_type || 'N/A'}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                              {/* R{product.unit_price.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} */}
                                              {formatZARCurrency(product.unit_price)}
                                            </TableCell>
                                            <TableCell>
                                              <div className="flex items-center glass-input rounded-md overflow-hidden w-20">
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => {
                                                    const currentQty = parseInt(productQuantities[product.id] || '0');
                                                    if (currentQty > 1) {
                                                      setProductQuantities({
                                                        ...productQuantities,
                                                        [product.id]: (currentQty - 1).toString()
                                                      });
                                                    }
                                                  }}
                                                  disabled={!productQuantities[product.id] || parseInt(productQuantities[product.id]) <= 1}
                                                  className="h-6 w-6 p-0 hover:bg-muted rounded-none"
                                                >
                                                  <span className="font-bold text-lg leading-none">−</span>
                                                </Button>
                                                <Input
                                                  type="number"
                                                  min="1"
                                                  placeholder="0"
                                                  value={productQuantities[product.id] || ''}
                                                  onChange={(e) => setProductQuantities({
                                                    ...productQuantities,
                                                    [product.id]: e.target.value
                                                  })}
                                                  className="border-0 text-center w-8 h-6 p-0 focus:ring-0 bg-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                                                />
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => {
                                                    const currentQty = parseInt(productQuantities[product.id] || '0');
                                                    setProductQuantities({
                                                      ...productQuantities,
                                                      [product.id]: (currentQty + 1).toString()
                                                    });
                                                  }}
                                                  className="h-6 w-6 p-0 hover:bg-muted rounded-none"
                                                >
                                                  <Plus className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => togglePriceReview(product.id)}
                                                className={`h-8 w-8 p-0 rounded-md transition-all ${reviewedProducts.has(product.id)
                                                  ? 'bg-worldsview-green/20 hover:bg-worldsview-green/30'
                                                  : 'hover:bg-muted/30'
                                                  }`}
                                                title={reviewedProducts.has(product.id) ? 'Remove from price review' : 'Mark for price review'}
                                              >
                                                {reviewedProducts.has(product.id) ? (
                                                  <CheckCircle className="w-4 h-4 text-worldsview-green" />
                                                ) : (
                                                  <Circle className="w-4 h-4 text-muted-foreground" />
                                                )}
                                              </Button>
                                            </TableCell>
                                            <TableCell>
                                              <Button
                                                size="sm"
                                                onClick={() => addProductToOpportunity(product)}
                                                disabled={!productQuantities[product.id] || parseInt(productQuantities[product.id]) < 1}
                                                className="bg-worldsview-green hover:bg-worldsview-green/90 h-8"
                                              >
                                                <Plus className="w-3 h-3" />
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                  {priceList.length === 0 && (
                                    <div className="p-8 text-center text-muted-foreground">
                                      <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                      <p>No products found matching your search criteria.</p>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>

                            {/* Product Lines List */}
                            {newOpportunity.productLines.length > 0 && (
                              <Card className="glass-card">
                                <CardHeader>
                                  <CardTitle className="text-lg">Product Lines</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="w-28 min-w-28">SKU</TableHead>
                                        <TableHead className="w-120 min-w-16">Product</TableHead>
                                        <TableHead className="w-16 min-w-16">Term</TableHead>
                                        <TableHead className="w-16 min-w-16">Product Group</TableHead>
                                        <TableHead className="w-24 min-w-24">Qty</TableHead>
                                        <TableHead className="w-24 min-w-24">Unit Price</TableHead>
                                        <TableHead className="w-24 min-w-24">Total</TableHead>
                                        <TableHead className="text-center">Price Review</TableHead>
                                        <TableHead></TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {newOpportunity.productLines.map((line) => {
                                        // Check if this product line was selected for price review
                                        const wasReviewed = Array.from(reviewedProducts).some(productId => {
                                          const catalogProduct = priceList.find(p => p.id === productId);
                                          return catalogProduct && catalogProduct.description === line.product;
                                        });

                                        return (
                                          <TableRow key={line.id}>
                                            <TableCell className="font-mono text-sm">{line.item_id || 'N/A'}</TableCell>
                                            <TableCell className="font-medium truncate max-w-80">{line.product_name}</TableCell>
                                            <TableCell>
                                              <Badge variant="outline" className="glass-badge text-xs">
                                                {line.contract_term || 'N/A'}
                                              </Badge>
                                            </TableCell>
                                            <TableCell>
                                              <Badge
                                                variant="secondary"
                                                className="glass-badge text-xs"
                                              >
                                                {line.item_group || 'N/A'}
                                              </Badge>
                                            </TableCell>
                                            <TableCell>{line.quantity}</TableCell>
                                            <TableCell>{formatZARCurrency(line.unit_price)}</TableCell>
                                            <TableCell className="font-medium">{formatZARCurrency(line.total)}</TableCell>
                                            <TableCell className="text-center">
                                              {line.price_review ? (
                                                <Badge
                                                  variant="outline"
                                                  className="bg-worldsview-green/10 text-worldsview-green border-worldsview-green/20 text-xs"
                                                >
                                                  <CheckCircle className="w-3 h-3" />
                                                </Badge>
                                              ) : (
                                                <Badge
                                                  variant="outline"
                                                  className="bg-muted/10 text-muted-foreground border-muted/20 text-xs"
                                                >
                                                  <Circle className="w-3 h-3" />
                                                </Badge>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeProductLine(line.id)}
                                                className="text-worldsview-red hover:text-worldsview-red/80"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                      <TableRow>
                                        <TableCell colSpan={7} className="text-right font-medium">
                                          Total Opportunity Value:
                                        </TableCell>
                                        <TableCell className="font-bold text-lg">
                                          {formatZARCurrency(calculateOpportunityTotal().toFixed(2))}
                                        </TableCell>
                                        <TableCell></TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </CardContent>
                              </Card>
                            )}

                            <div className="flex justify-between pt-6">
                              <Button variant="outline" onClick={prevStep}>
                                Previous
                              </Button>
                              <div className="flex gap-2">
                                {/* <Button variant="outline" onClick={saveAsDraft}>
                                  Save as Draft
                                </Button> */}
                                <Button
                                  onClick={nextStep}
                                  disabled={newOpportunity.productLines.length === 0}
                                  className="bg-worldsview-blue hover:bg-worldsview-blue/90"
                                >
                                  Next: Sign-off
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Step 4: Sign-off */}
                        {currentStep === 4 && (
                          <div className="space-y-6">
                            <div>
                              <h3 className="text-lg font-semibold mb-2">Step 4: Sign-off</h3>
                              <p className="text-muted-foreground mb-6">Review and confirm the opportunity details.</p>
                            </div>

                            {/* Opportunity Summary */}
                            <Card className="glass-card">
                              <CardHeader>
                                <CardTitle>Opportunity Summary</CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-6">
                                  <div>
                                    <h4 className="font-medium mb-2">Basic Information</h4>
                                    <div className="space-y-2 text-sm">
                                      <div><strong>ID:</strong> {newOpportunity.id}</div>
                                      <div><strong>{newOpportunity.name}</strong></div>
                                      <div><strong>Customer:</strong> {selectedCustomer?.name}</div>
                                      <div><strong>Contact:</strong> {selectedCustomer.contacts.filter((contact) => contact.person_id === selectedContact).map((contact) => `${contact.first_name} ${contact.last_name}`)}</div>
                                      <div><strong>Driver Detail:</strong> {newOpportunity.driver_id}</div>
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-medium mb-2">Timeline & Value</h4>
                                    <div className="space-y-2 text-sm">
                                      <div><strong>Transaction Start:</strong> {formatDate(newOpportunity.transaction_start_date)}</div>
                                      <div><strong>Expected Close:</strong> {formatDate(newOpportunity.expiry_date)}</div>
                                      <div><strong>Total Value:</strong> {formatZARCurrency(calculateOpportunityTotal())}</div>
                                      <div><strong>Product Lines:</strong> {newOpportunity.productLines.length}</div>
                                    </div>
                                  </div>
                                </div>
                                {newOpportunity.description && (
                                  <div>
                                    <h4 className="font-medium mb-2">Description</h4>
                                    <p className="text-sm text-muted-foreground">{newOpportunity.partner_comments}</p>
                                  </div>
                                )}
                                {newOpportunity.productLines.length > 0 && (
                                  <div>
                                    <h4 className="font-medium mb-2">Selected Products</h4>
                                    <div className="space-y-2">
                                      {newOpportunity.productLines.map((line) => (
                                        <div key={line.id} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                              <div className="font-medium text-sm">{line.product}</div>
                                              {line.contractTerm && (
                                                <Badge
                                                  variant="outline"
                                                  className={`glass-badge text-xs font-medium ${line.contractTerm === 'Monthly' ? 'bg-worldsview-blue/10 border-worldsview-blue text-worldsview-blue' :
                                                    line.contractTerm === 'Annual' ? 'bg-worldsview-green/10 border-worldsview-green text-worldsview-green' :
                                                      line.contractTerm === '2 Year' ? 'bg-worldsview-purple/10 border-worldsview-purple text-worldsview-purple' :
                                                        line.contractTerm === '3-Year' || line.contractTerm === 'Triennial' ? 'bg-worldsview-orange/10 border-worldsview-orange text-worldsview-orange' :
                                                          'bg-muted/10 border-muted-foreground text-muted-foreground'
                                                    }`}
                                                >
                                                  {line.contractTerm}
                                                </Badge>
                                              )}
                                              {line.needsPriceReview && (
                                                <Badge
                                                  variant="outline"
                                                  className="glass-badge text-xs font-medium bg-worldsview-orange/10 border-worldsview-orange text-worldsview-orange flex items-center gap-1"
                                                >
                                                  <AlertTriangle className="w-3 h-3" />
                                                  Price Review
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="font-medium">{line.product_name}</div>
                                            <div className="text-xs text-muted-foreground">{line.item_id}</div>
                                          </div>
                                          <div className="text-right text-sm">
                                            <div className="font-medium">Qty: {line.quantity}</div>
                                            <div className="text-muted-foreground">{formatZARCurrency(line.total)}</div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>

                            <div className="flex justify-between pt-6">
                              <Button variant="outline" onClick={prevStep}>
                                Previous
                              </Button>
                              <div className="flex gap-2">
                                <Button
                                  onClick={handleCreateOpportunity}
                                  className="bg-worldsview-green hover:bg-worldsview-green/90"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Create Opportunity
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Search and Filters */}
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search opportunities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 glass-input"
                  />
                </div>
                <Select value={filterStage} onValueChange={setFilterStage}>
                  <SelectTrigger className="w-64 glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    {processStages.map(stage => (
                      <SelectItem key={`filter_stage_${stage.id}`} value={stage.value}>
                        {stage.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterEvent} onValueChange={setFilterEvent}>
                  <SelectTrigger className="w-48 glass-input">
                    <SelectValue placeholder="All Events" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    {events.map(event => (
                      <SelectItem key={`_event_${event}`} value={event}>
                        {event}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </div>

              {/* Opportunities Table */}
              <div className="rounded-md border glass-input">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[200px] md:w-auto whitespace-normal">Opportunity</TableHead>
                      <TableHead className="hidden lg:table-cell md:w-auto whitespace-normal">Autodesk Event</TableHead>
                      <TableHead className="min-w-[150px] w-auto whitespace-normal">Contract</TableHead>
                      <TableHead className="min-w-[200px] lg:min-w-[350px] w-auto">Stage</TableHead>
                      <TableHead className="min-w-[100px] text-right whitespace-normal">Qty</TableHead>
                      <TableHead className="min-w-[150px] text-right whitespace-normal">Value</TableHead>
                      <TableHead className="hidden lg:table-cell md:w-auto whitespace-normal">Sales Status</TableHead>
                      <TableHead className="hidden lg:table-cell md:w-auto whitespace-normal">Sales Order</TableHead>
                      <TableHead className="min-w-[150px] md:w-auto whitespace-normal">Expected close date</TableHead>
                      <TableHead className="min-w-[150px] md:w-auto whitespace-normal">Created by</TableHead>
                      <TableHead className="min-w-[100px] whitespace-normal">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center">
                          <LoadingSpinner />
                        </TableCell>
                      </TableRow>
                    ) : localOpportunities.length > 0 ? (
                      localOpportunities.map((opp) => {
                        const isClosed = opp.process_stage_name === '06-Closed / Won' || opp.process_stage_name === '07-Closed / Lost - Cancelled';
                        const totalQty = opp.lines?.reduce((sum, line) => sum + (Number(line.qty) || 0), 0) || 0;

                        return (
                          <TableRow key={opp.id} className={!opp.d365_rec_id ? 'bg-gray-300' : ''}>
                            <TableCell className="whitespace-normal md:w-auto">
                              <div>
                                <div className="font-medium">{opp?.end_user?.name}</div>
                                <div className="text-sm text-muted-foreground">{opp.sales_quotation_number}</div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell md:w-auto whitespace-normal">
                              <span className="text-muted-foreground">
                                {opp.event || '-'}
                              </span>
                            </TableCell>
                            
                            <TableCell className="whitespace-normal">
                              {opp.contract ? (
                                <span className="font-medium">{opp.contract}</span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-normal">
                              {isClosed ? (
                                <Badge
                                  variant="outline"
                                  className={`glass-badge font-medium ${getStageBadgeColor(opp.process_stage_name)} space-y-4`}
                                >
                                  {opp.process_stage_name}
                                </Badge>
                              ) : (
                                <Select className="w-full bg-red-500"
                                  value={opp.process_stage_name}
                                  onValueChange={(value) => handleStageChange(opp.id, value, opp.opportunity_id)}
                                  disabled={!opp.d365_rec_id}
                                >
                                  <SelectTrigger className={`w-30 glass-input border ${getStageBadgeColor(opp.process_stage_name)}`}>
                                    <SelectValue>
                                      <span className="font-medium">{opp.process_stage_name}</span>
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {processStages.map(stage => (
                                      <SelectItem key={`${opp.id}_stage_${stage.id}`} value={stage.value}>
                                        {stage.value}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-normal text-right">
                              <span className="text-muted-foreground text-right">{totalQty > 0 ? totalQty : '-'}</span>
                            </TableCell>
                            <TableCell className="whitespace-normal font-medium text-right">{Number(opp.opportunity_value).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="hidden md:table-cell md:w-auto whitespace-normal">
                              {opp.process_stage_name === '06-Closed / Won' && opp.portal_status && (
                                <Badge
                                  variant="outline"
                                  className={`glass-badge font-medium ${opp.portal_status === 'Open order'
                                    ? 'bg-worldsview-blue/10 border-worldsview-blue text-worldsview-blue'
                                    : opp.portal_status === 'Delivered'
                                      ? 'bg-worldsview-orange/10 border-worldsview-orange text-worldsview-orange'
                                      : opp.portal_status === 'Invoiced'
                                        ? 'bg-worldsview-green/10 border-worldsview-green text-worldsview-green'
                                        : 'bg-muted/10 border-muted-foreground text-muted-foreground'
                                    }`}
                                >
                                  {opp.portal_status}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-normal md:w-auto">{opp.sales_id ? opp.sales_id : '-'}</TableCell>
                            <TableCell className="whitespace-normal md:w-auto">{opp.expiry_date}</TableCell>
                            <TableCell className="whitespace-normal md:w-auto">
                              <span className="text-muted-foreground">{opp.created_by || '-'}</span>
                            </TableCell>
                            <TableCell className="whitespace-normal">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title={!opp.d365_rec_id ? "Syncing with Dynamics 365..." : "View opportunity details"}
                                  onClick={() => {
                                    if (!opp.d365_rec_id) {
                                      toast.warning('This opportunity has not yet been synchronised with Dynamics 365 and is therefore not editable');
                                    } else {
                                      handleViewOpportunity(opp);
                                    }
                                  }}
                                >
                                  {!opp.d365_rec_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center">
                          No opportunities found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <div className="grid grid-cols-3 items-center p-4">
                  <div className="text-left">
                    <p className="text-sm text-muted-foreground">
                      Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalCount} opportunities)
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchOpportunities(idToken, currentPartner, pagination.currentPage - 1, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination)}
                      disabled={pagination.currentPage <= 1 || isLoading}
                    >
                      Previous
                    </Button>
                    {renderPageNumbers()}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchOpportunities(idToken, currentPartner, pagination.currentPage + 1, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination)}
                      disabled={pagination.currentPage >= pagination.totalPages || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                  <div className="text-right"></div> {/* Empty div for alignment */}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* End User Accounts Tab */}
        <EndUserAccountsTab />
      </Tabs>

      {/* View Opportunity Dialog */}
      <Dialog open={isViewOpportunityOpen} onOpenChange={(open) => {
        setIsViewOpportunityOpen(open);
        if (!open) {
          setIsViewDialogInEditMode(false);
          setViewDialogEditData(null);
        }
      }}>
        <DialogContent
          className="!w-[90vw] !max-w-none max-h-[95vh] glass-modal flex flex-col"
          style={{ width: '90vw', maxWidth: 'none' }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isViewDialogInEditMode ? (
                <>
                  <Edit className="w-5 h-5 text-worldsview-purple" />
                  Edit Opportunity
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5 text-worldsview-blue" />
                  Opportunity Details
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {isViewDialogInEditMode
                ? 'Make changes to the opportunity details below.'
                : 'View complete details and product information for this opportunity.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end -mt-14 relative z-10">
            {!isViewDialogInEditMode && (
              <Button
                variant="outline"
                onClick={handleEnableViewDialogEdit}
                className="glass-button"
                disabled={viewOpportunity?.process_stage_name === '06-Closed / Won' || viewOpportunity?.process_stage_name === '07-Closed / Lost - Cancelled'}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>

          {viewOpportunity && (
            <div className="space-y-6 overflow-y-auto flex-1 min-h-0">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <Label className="text-sm text-muted-foreground">Sales Quotation Number</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{viewOpportunity.sales_quotation_number}</p>
                    <button
                      onClick={() => handleCopyToClipboard(viewOpportunity.sales_quotation_number, 'Sales Quotation Number')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Opportunity ID</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{viewOpportunity.opportunity_id}</p>
                    <button
                      onClick={() => handleCopyToClipboard(viewOpportunity.opportunity_id, 'Opportunity ID')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                {viewOpportunity.sales_id &&
                <div>
                  <Label className="text-sm text-muted-foreground">Sales Order ID</Label>
                  <p className="font-medium">{viewOpportunity.sales_id}</p>
                </div>
                }
                <div>
                  <Label className="text-sm text-muted-foreground">Autodesk Opportunity ID</Label>
                  {isViewDialogInEditMode ? (
                    <Input
                        type="text"
                        value={viewDialogEditData?.autodesk_opportunity_id}
                        onChange={(e) => setViewDialogEditData({ ...viewDialogEditData, autodesk_opportunity_id: e.target.value })}
                        className="glass-input"
                      />
                  ) : (
                    <p className="font-medium">{viewOpportunity.autodesk_opportunity_id}</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm text-muted-foreground">Stage</Label>
                  {isViewDialogInEditMode ? (
                    <>
                      <Badge
                        variant="outline"
                        className={`glass-badge font-medium ${getStageBadgeColor(viewOpportunity.process_stage_name)}`}
                      >
                        {viewOpportunity.process_stage_name}
                      </Badge>
                    </>
                  ) : (
                    <>
                      <Badge
                        variant="outline"
                        className={`glass-badge font-medium ${getStageBadgeColor(viewOpportunity.process_stage_name)}`}
                      >
                        {viewOpportunity.process_stage_name}
                      </Badge>
                    </>
                  )}
                </div>
              </div>

              {/* Customer Information */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-worldsview-blue" />
                  Customer Information
                </h3>
                <div className="space-y-4">
                  {/* Row 1: Customer Name | Contact Person */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Customer Name</Label>
                      <p className="font-medium">{viewOpportunity.end_user.name}</p>
                      <Label className="text-sm text-muted-foreground mt-3 block">Customer Number</Label>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{viewOpportunity?.end_user?.end_user_id || '-'}</p>
                        {viewOpportunity?.end_user?.end_user_id && (
                          <button
                            onClick={() => handleCopyToClipboard(String(viewOpportunity.end_user.end_user_id), 'Customer Number')}
                            className="p-1 hover:bg-muted rounded transition-colors"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3 text-muted-foreground" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Contact</Label>
                      {isViewDialogInEditMode ? (
                        <Select
                          value={viewDialogEditData.contact_person_id}
                          onValueChange={(value) => setViewDialogEditData({ ...viewDialogEditData, contact_person_id: value })}
                        >
                          <SelectTrigger className="glass-input">
                            <SelectValue placeholder="Select contact person">
                              {(() => {
                                const selectedContact = endUserContacts.find(
                                  c => c.person_id === viewDialogEditData.contact_person_id
                                );
                                if (selectedContact) {
                                  return (
                                    <div className="flex items-center gap-2 text-left">
                                      <span className="font-medium">{selectedContact.first_name} {selectedContact.last_name}</span>
                                      <span className="text-muted-foreground text-sm">{selectedContact.email}</span>
                                      <span className="text-muted-foreground text-sm">{selectedContact.phone_number}</span>
                                    </div>
                                  );
                                }
                                return viewDialogEditData.contact_person_id;
                              })()}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {(() => {
                              if (endUserContacts && endUserContacts.length > 0) {
                                return endUserContacts.map((contact) => (
                                  <SelectItem key={contact.person_id} value={contact.person_id} className="cursor-pointer">
                                    <div className="flex flex-col py-1">
                                      <span className="font-medium">{contact.first_name} {contact.last_name}</span>
                                      <div className="flex items-center gap-3 mt-0.5">
                                        <span className="text-sm text-muted-foreground">{contact.email}</span>
                                        <span className="text-sm text-muted-foreground">{contact.phone_number}</span>
                                      </div>
                                    </div>
                                  </SelectItem>
                                ));
                              }
                              return [
                                <SelectItem key="no-contacts" value={viewDialogEditData.contactPerson} disabled>
                                  No contacts available for this customer
                                </SelectItem>
                              ];
                            })()}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium">{viewOpportunity?.contact?.first_name} {viewOpportunity?.contact?.last_name}</p>
                      )}
                    </div>
                    
                  </div>

                  {/* Row 2: Customer Reference | Email + Phone */}
                  <div className="grid grid-cols-2 gap-4">
                    
                      <div>
                        <Label className="text-sm text-muted-foreground">Email</Label>
                        <p className="font-medium">{viewOpportunity?.contact?.email}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Phone</Label>
                        <p className="font-medium">{viewOpportunity?.contact?.phone_number || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">CSN</Label>
                        <p className="font-medium">{viewOpportunity?.end_user?.csn || '-'}</p>
                      </div>
                    
                  </div>
                </div>
              </div>

              {/* Opportunity Details */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Target className="w-4 h-4 text-worldsview-purple" />
                  Opportunity Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Value</Label>
                    <p className="font-medium text-worldsview-green">
                      {isViewDialogInEditMode ? formatZARCurrency(viewDialogEditData.opportunity_value) : formatZARCurrency(viewOpportunity.opportunity_value)}
                    </p>
                    {isViewDialogInEditMode && (
                      <p className="text-xs text-muted-foreground mt-1">Calculated from product lines</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Transaction Start Date</Label>
                      {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() != 'renewal' ? (
                        <Input
                          type="date"
                          value={viewDialogEditData.transaction_start_date}
                          onChange={(e) => setViewDialogEditData({ ...viewDialogEditData, transaction_start_date: e.target.value })}
                          min={getMinDate()}
                          max={getMaxDate()}
                          className="glass-input"
                        />
                      ) : (
                        <p className="font-medium">{viewOpportunity.transaction_start_date}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Expected Close Date</Label>
                      {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() != 'renewal' ? (
                        <Input
                          type="date"
                          min={getMinDate()}
                          value={viewDialogEditData.expiry_date}
                          onChange={(e) => setViewDialogEditData({ ...viewDialogEditData, expiry_date: e.target.value })}
                          className="glass-input"
                        />
                      ) : (
                        <p className="font-medium">{viewOpportunity.expiry_date}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-muted-foreground">Driver Details</Label>
                    {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() != 'renewal' ? (
                      <>
                        <Select
                          value={viewDialogEditData.driver_id}
                          onValueChange={(value) => setViewDialogEditData({ ...viewDialogEditData, driver_id: value })}
                        >
                          <SelectTrigger className="glass-input">
                            <SelectValue placeholder="Select driver detail" />
                          </SelectTrigger>
                          <SelectContent>
                            {driverDetailoptions.map((option) => (
                              <SelectItem key={option.id} value={option.name}>
                                {option.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    ) : (
                      <p className="font-medium">{viewOpportunity.driver_id || ''}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Created By</Label>
                    <p className="font-medium">{isViewDialogInEditMode ? viewDialogEditData.created_by || '-' : viewOpportunity.created_by || '-'}</p>
                  </div>
                  
                  <div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Contract</Label>
                      <p className="font-medium">{viewOpportunity.contract}</p>
                      {viewOpportunity.event?.toLowerCase() == 'renewal' && !isViewDialogInEditMode &&(
                        <>
                          <div>
                            <Label className="text-sm text-muted-foreground">Contract end date</Label>
                            <p className="font-medium">{viewOpportunity.default_contract_end_date}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Contract renewal date</Label>
                            <p className="font-medium">{`${viewOpportunity.proposed_contract_end_date}`}</p>
                          </div>
                        </>
                      )}
                    </div>
                    {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() == 'renewal' && (
                      <div className="flex gap-4">
                        <div>
                          <Label className="text-sm text-muted-foreground">Contract end date</Label>
                          <p className="font-medium">{viewOpportunity.default_contract_end_date}</p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Contract renewal date</Label>
                          <p className="font-medium">{`${viewOpportunity.proposed_contract_end_date}`}</p>
                        </div>
                        <div>
                          <Button
                            onClick={() => {
                              setIsAdjustContractDialogOpen(true);
                              setProposedContractEndDate(viewDialogEditData.proposed_contract_end_date ? new Date(viewDialogEditData.proposed_contract_end_date) : new Date());
                            }}
                            size="sm"
                            className="bg-[#7AC142] hover:bg-[#6ab032] text-white border-none shadow-lg"
                          >Adjust contract renewal end date</Button>
                      </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Autodesk Event</Label>
                    {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() != 'renewal' ? (
                      <Select
                        value={viewDialogEditData.event || ''}
                        onValueChange={(value) => setViewDialogEditData({ ...viewDialogEditData, event: value })}
                      >
                        <SelectTrigger className="glass-input">
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Initial order">Initial order</SelectItem>
                          <SelectItem value="Additions">Addition</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">{viewOpportunity.event || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Created Date</Label>
                    <p className="font-medium">{isViewDialogInEditMode ? viewDialogEditData.created_at : viewOpportunity.created_at}</p>
                  </div>
                  {((isViewDialogInEditMode && viewDialogEditData.process_stage_name === '06-Closed Won') ||
                    (!isViewDialogInEditMode && viewOpportunity.process_stage_name === '06-Closed Won' && viewOpportunity.portal_status)) && (
                      <div>
                        <Label className="text-sm text-muted-foreground">Sales Status</Label>
                        {isViewDialogInEditMode ? (
                          <Select
                            value={viewDialogEditData.portal_status || ''}
                            onValueChange={(value) => setViewDialogEditData({ ...viewDialogEditData, portal_status: value })}
                          >
                            <SelectTrigger className="glass-input">
                              <SelectValue placeholder="Select sales status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Open order">Open order</SelectItem>
                              <SelectItem value="Delivered">Delivered</SelectItem>
                              <SelectItem value="Invoiced">Invoiced</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge
                            variant="outline"
                            className={`glass-badge font-medium ${viewOpportunity.portal_status === 'Open order'
                              ? 'bg-worldsview-blue/10 border-worldsview-blue text-worldsview-blue'
                              : viewOpportunity.portal_status === 'Delivered'
                                ? 'bg-worldsview-orange/10 border-worldsview-orange text-worldsview-orange'
                                : viewOpportunity.portal_status === 'Invoiced'
                                  ? 'bg-worldsview-green/10 border-worldsview-green text-worldsview-green'
                                  : 'bg-muted/10 border-muted-foreground text-muted-foreground'
                              }`}
                          >
                            {viewOpportunity.portal_status}
                          </Badge>
                        )}
                      </div>
                    )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Partner Reference</Label>
                  {isViewDialogInEditMode ? (
                    <Input
                      value={viewDialogEditData.partner_reference || ''}
                      onChange={(e) => setViewDialogEditData({ ...viewDialogEditData, partner_reference: e.target.value })}
                      className="glass-input"
                      placeholder="Enter partner reference"
                    />
                  ) : (
                    <p className="font-medium">{viewOpportunity.partner_reference}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Customer Reference</Label>
                  {isViewDialogInEditMode ? (
                    <Input
                      value={viewDialogEditData.customer_reference || ''}
                      onChange={(e) => setViewDialogEditData({ ...viewDialogEditData, customer_reference: e.target.value })}
                      className="glass-input"
                      placeholder="Enter customer reference"
                    />
                  ) : (
                    <p className="font-medium">{viewOpportunity.customer_reference || '-'}</p>
                  )}
                </div>
              </div>

              {/* Description & Notes */}
              <div className="space-y-3">
                {(isViewDialogInEditMode || viewOpportunity.partner_comments) && (
                  <div>
                    <Label className="text-sm text-muted-foreground">Partner comments</Label>
                    {isViewDialogInEditMode ? (
                      <Textarea
                        value={viewDialogEditData.partner_comments || ''}
                        onChange={(e) => setViewDialogEditData({ ...viewDialogEditData, partner_comments: e.target.value })}
                        className="glass-input mt-1"
                        rows={3}
                      />
                    ) : (
                      <p className="mt-1 text-sm">{viewOpportunity.partner_comments}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Product Lines */}
              {viewOpportunity.lines && viewOpportunity.lines.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium flex items-center gap-2">
                      <Package className="w-4 h-4 text-worldsview-green" />
                      Product Lines
                    </h3>
                    {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() != 'renewal' && (
                      <div className="flex items-center gap-3">
                        <Button
                          onClick={() => {
                            if (setActiveSection) {
                              setCurrentOpportunityId(viewOpportunity.id);
                              setActiveSection('Price Lookup');
                            }
                            setIsViewOpportunityOpen(false);
                          }}
                          size="default"
                          className="bg-[#7AC142] hover:bg-[#6ab032] text-white border-none shadow-lg"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Lines
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>SKU</TableHead>
                          <TableHead>Product</TableHead>
                          <TableHead>Term</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Unit Price</TableHead>
                          <TableHead>Batch</TableHead>
                          <TableHead>Months</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead>Price Review</TableHead>
                          {isViewDialogInEditMode && <TableHead className="w-[50px]"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(isViewDialogInEditMode ? viewDialogEditData.lines : viewOpportunity.lines).filter(it => Number(it.qty) > 0).map((line) => (
                          <TableRow key={line.id}>
                            <TableCell className="font-medium">{line.item_id || '-'}</TableCell>
                            <TableCell className="font-medium">{line.description}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="glass-badge text-xs">
                                {line.autodesk_months || line.months || '-'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() != 'renewal' ? (
                                <div className="flex items-center glass-input rounded-md overflow-hidden w-20">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleUpdateProductLineQuantity(line.id, Math.max(1, Number(line.qty) - 1))}
                                    disabled={line.qty <= 1}
                                    className="h-6 w-6 p-0 hover:bg-muted rounded-none"
                                  >
                                    <span className="font-bold text-lg leading-none">−</span>
                                  </Button>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={Math.round(line.qty)}
                                    onChange={(e) => handleUpdateProductLineQuantity(line.id, parseInt(e.target.value) || 1)}
                                    className="border-0 text-center w-8 h-6 p-0 focus:ring-0 bg-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleUpdateProductLineQuantity(line.id, Number(line.qty) + 1)}
                                    className="h-6 w-6 p-0 hover:bg-muted rounded-none"
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                Math.round(line.qty)
                              )}
                            </TableCell>
                            <TableCell>
                              {formatZARCurrency(line.price)}
                            </TableCell>
                            <TableCell className="font-medium">{line.batch ? line.batch : '-'}</TableCell>
                            <TableCell className="font-medium">{line.months && Number(line.months) > 0 ? line.months : '-'}</TableCell>
                            <TableCell className="font-medium">{formatZARCurrency(line.amount)}</TableCell>
                            <TableCell className="text-center">
                              {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() != 'renewal' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTogglePriceReview(line.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  {line.price_review ? (
                                    <CheckCircle className="w-4 h-4 text-worldsview-green" />
                                  ) : (
                                    <Circle className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </Button>
                              ) : (
                                <>
                                  {line.price_review || line.wasReviewed || line.needsPriceReview ? (
                                    <Badge
                                      variant="outline"
                                      className="bg-worldsview-green/10 text-worldsview-green border-worldsview-green/20 text-xs"
                                    >
                                      <CheckCircle className="w-3 h-3" />
                                    </Badge>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="bg-muted/10 text-muted-foreground border-muted/20 text-xs"
                                    >
                                      <Circle className="w-3 h-3" />
                                    </Badge>
                                  )}
                                </>
                              )}
                            </TableCell>
                            {isViewDialogInEditMode && viewDialogEditData.event?.toLowerCase() != 'renewal' && (
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveProductLine(line.id)}
                                  className="h-8 w-8 p-0 text-worldsview-red hover:bg-worldsview-red/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            
              {/* Action Buttons (Edit Mode) */}
              {isViewDialogInEditMode && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelViewDialogEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-worldsview-blue hover:bg-worldsview-blue/90"
                    onClick={() =>handleSaveViewDialogEdit(viewDialogEditData)}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Contract Date Dialog */}
      <AdjustContractDialog
        isOpen={isAdjustContractDialogOpen}
        onOpenChange={setIsAdjustContractDialogOpen}
        proposedContractEndDate={proposedContractEndDate}
        onDateChange={setProposedContractEndDate}
        onSubmit={handleAdjustContractDate}
        formatDateToISO={formatDateToISO}
      />

      {/* Create End User Dialog (from Opportunity Creation) */}
      <CreateEndUserDialog
        isOpen={isCreateEndUserFromOpportunityOpen}
        onOpenChange={setIsCreateEndUserFromOpportunityOpen}
        newEndUser={newEndUser}
        onEndUserChange={setNewEndUser}
        segments={segments}
        countries={countries}
        onSubmit={handleCreateEndUserFromOpportunity}
        onCancel={() => {
          setIsCreateEndUserFromOpportunityOpen(false);
          setNewEndUser({
            name: '',
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            address_street: '',
            address_city: '',
            address_zip_code: '',
            address_country_region_id: 'ZAF',
            industry: '',
            licenses: 0,
            status: 'Active',
            csn: ''
          });
        }}
      />

      {/* Opportunity Action Dialog (Lost/Cancelled) */}
      <OpportunityActionDialog
        isOpen={isActionDialogOpen}
        onOpenChange={setIsActionDialogOpen}
        selectedAction={selectedAction}
        selectedOpportunity={selectedOpportunityForAction}
        selectedReason={selectedReason}
        onReasonChange={setSelectedReason}
        cancelledReasons={cancelledReasons}
        onConfirm={handleConfirmAction}
        onCancel={() => {
          fetchOpportunities(idToken, currentPartner, 1, pagination.perPage, searchTerm, filterStage, filterEvent, setIsLoading, setLocalOpportunities, setPagination);
          setIsActionDialogOpen(false);
          setSelectedAction(null);
          setSelectedOpportunityForAction(null);
          setSelectedReason('');
        }}
      />

      {/* Stage 06 (Closed Won) Confirmation Dialog */}
      <Stage06Dialog
        isOpen={isStage06DialogOpen}
        onOpenChange={setIsStage06DialogOpen}
        selectedOpportunity={selectedOpportunityForAction}
        onAccept={handleStage06Accept}
        onDecline={handleStage06Decline}
      />

      {/* Stage 07 (Closed Lost) - Lost or Cancelled Dialog */}
      <Stage07Dialog
        isOpen={isStage07DialogOpen}
        onOpenChange={setIsStage07DialogOpen}
        selectedOpportunity={selectedOpportunityForAction}
        onLost={handleStage07Lost}
        onCancelled={handleStage07Cancelled}
        onCancel={handleStage07Cancel}
      />
    </div>
  );

}
